/**
 * Maps an NGT case-details API response into the Firestore case document
 * shape used across the portal (pending / disposed collections).
 *
 * Shared by NGTCaseList (bulk export) and NGTCaseDetails (single export).
 */

/**
 * Normalise any date string we get from NGT into the same dd/mm/yyyy format
 * AddCase writes (`String(day).padStart(2,"0")/...`). Accepts dd/mm/yyyy,
 * d/m/yyyy, dd-mm-yyyy, or d-m-yyyy. Returns "" on anything unrecognised so
 * Firestore never gets a half-parsed string.
 */
export const toAddCaseDate = (raw) => {
  if (!raw || typeof raw !== "string") return "";
  const m = raw.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!m) return "";
  return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3]}`;
};

/** Split "A vs B" / "A versus B" into [petitioner, respondent]. */
export const splitPartyName = (name) => {
  if (!name || typeof name !== "string") return ["", ""];
  const parts = name.split(/\s+(?:vs\.?|versus)\s+/i);
  return [(parts[0] || "").trim(), (parts[1] || "").trim()];
};

/**
 * Adapt a flat NGT judgement/order detail object into the
 * { caseInfo, parties, listingHistory } shape that buildNgtCaseData and
 * uploadNgtOrderPdfs expect, so a judgement/order exports through the exact
 * same path as a Case-Status case.
 *
 * The judgement detail keys are dynamic (underscored field labels scraped from
 * the page, e.g. "Filing_Number", "Petitioner_Advocate(s)"), so we match them
 * via a normalised lookup (lower-cased, non-alphanumerics stripped) rather than
 * assuming exact keys. The detail has no structured listing history — only a
 * `pdfLinks` array — so each PDF becomes a listing-history row (dated to
 * Last_Listed) purely so the order PDFs import into the case's Orders folder.
 *
 * @param {object} detail - the `data` object from getJudgementDetail()
 * @param {object} row    - the judgement search-results row that was clicked
 * @returns {{caseInfo:object, parties:object, listingHistory:Array}}
 */
export const judgementDetailToCaseDetails = (detail = {}, row = {}) => {
  const normKey = (k) => String(k).toLowerCase().replace(/[^a-z0-9]/g, "");
  const norm = {};
  Object.entries(detail).forEach(([k, v]) => {
    if (k === "pdfLinks") return;
    norm[normKey(k)] = v;
  });
  const pick = (...labels) => {
    for (const label of labels) {
      const v = norm[normKey(label)];
      if (v != null && v !== "") return v;
    }
    return "";
  };

  const caseInfo = {
    caseNumber: pick("Case Number") || row.caseNumber || "",
    filingNumber: pick("Filing Number") || row.diaryNumber || "",
    filingDate: pick("Filing Date"),
    registeredOn: pick("Registered On"),
    lastListed: pick("Last Listed"),
    partyName: pick("Party Name") || row.partyName || "",
    petitionerAdvocate: pick("Petitioner Advocate(s)", "Petitioner Advocate"),
    respondentAdvocate: pick("Respondent Advocate(s)", "Respondent Advocate"),
    act: pick("Act"),
    caseStatus: pick("Case Status") || row.status || "",
  };

  const pdfLinks = Array.isArray(detail.pdfLinks) ? detail.pdfLinks : [];
  const listingHistory = pdfLinks
    .filter((p) => p && p.url)
    .map((p, i) => ({
      sNo: i + 1,
      dateOfListing: caseInfo.lastListed || "",
      dateOfUpload: "",
      coram: [],
      pdfPath: p.url,
    }));

  return { caseInfo, parties: { petitioners: [], respondents: [] }, listingHistory };
};

/**
 * Build the Firestore case document from an NGT details response.
 *
 * Date handling:
 *  - All dates are normalised via toAddCaseDate so they match what AddCase
 *    writes (dd/mm/yyyy, zero-padded). The rest of the portal — event
 *    creation, edit flows, CaseDetails display — assumes that format.
 *  - NGT exposes no dedicated "next hearing" field; we treat `lastListed`
 *    as the next hearing date. If it's a future date, scheduleHearingReminders
 *    will create the 8 AM + 6 PM events; if it's past, those calls no-op.
 *  - previousHearingDate is set to the same value, so the UI can label one
 *    row "Last Listed" and another "Next Hearing" and both show the same
 *    figure without divergence.
 *
 * @param {object} caseDetails - response from getNgtCaseDetails()
 * @param {object} listItem    - the row from the search results table
 * @param {object} user        - firebase auth user
 * @param {string} searchType  - "party-name" | "case-number" | "free-text"
 * @param {Array}  cookies     - NGT session cookies (stored for traceability)
 * @returns {{caseData: object, collectionStatus: "pending"|"disposed"}}
 */
export const buildNgtCaseData = (caseDetails, listItem = {}, user, searchType, cookies) => {
  const ci = caseDetails?.caseInfo || {};
  const parties = caseDetails?.parties || {};
  const petitioners = Array.isArray(parties.petitioners) ? parties.petitioners : [];
  const respondents = Array.isArray(parties.respondents) ? parties.respondents : [];
  const listingHistory = Array.isArray(caseDetails?.listingHistory) ? caseDetails.listingHistory : [];

  const [petFromName, resFromName] = splitPartyName(ci.partyName || listItem?.partyName || "");
  const petitionerName = petitioners[0] || petFromName || "N/A";
  const respondentName = respondents[0] || resFromName || "N/A";

  const caseStatusStr = String(ci.caseStatus || listItem?.status || "");
  const collectionStatus = /dispos/i.test(caseStatusStr) ? "disposed" : "pending";

  // lastListed is the single source of truth for both "Last Listed" and
  // "Next Hearing" in the NGT context.
  const hearingDate = toAddCaseDate(ci.lastListed);
  const filedDate = toAddCaseDate(ci.filingDate || ci.registeredOn || listItem?.registrationDate);

  const caseHistory = listingHistory.map((h) => ({
    hearingDate: toAddCaseDate(h.dateOfListing),
    judge: Array.isArray(h.coram) ? h.coram.join(", ") : h.coram || "",
    purpose: "",
    causeListType: "",
    businessOnDate: toAddCaseDate(h.dateOfUpload),
    pdfPath: h.pdfPath || "",
  }));

  const caseData = {
    caseNo: ci.caseNumber || listItem?.caseNumber || "",
    filingNumber: ci.filingNumber || listItem?.diaryNumber || "",
    caseType: ci.act || "",
    caseFiledDate: filedDate,
    nextHearingDate: hearingDate,
    previousHearingDate: hearingDate,
    courtName: listItem?.zonalBench
      ? `National Green Tribunal - ${listItem.zonalBench}`
      : "National Green Tribunal",
    petitionerName,
    partyContactNo: "",
    respondentName,
    appearingFor: "",
    petitionerAdvocate: ci.petitionerAdvocate || "",
    respondentAdvocate: ci.respondentAdvocate || "",
    allPetitionerAdvocates: ci.petitionerAdvocate ? [ci.petitionerAdvocate] : [],
    allRespondentAdvocates: ci.respondentAdvocate ? [ci.respondentAdvocate] : [],
    team: [user.uid],
    files: [],
    casenotes: "",
    status: collectionStatus,
    owner: user.uid,
    caseHistory,
    orders: [],
    categoryDetails: {},
    payload: {
      searchType,
      caseUrl: listItem?.caseUrl || "",
      cookies: cookies || [],
      exportedAt: new Date().toISOString(),
      caseexportedfrom: "NGT",
      // NGT-specific raw fields preserved for richer rendering in CaseDetails.
      // Dates here are normalised to dd/mm/yyyy so any consumer reading from
      // payload.ngt sees the same format as the top-level case fields.
      ngt: {
        act: ci.act || "",
        caseNumber: ci.caseNumber || "",
        filingNumber: ci.filingNumber || "",
        filingDate: toAddCaseDate(ci.filingDate),
        registeredOn: toAddCaseDate(ci.registeredOn),
        lastListed: hearingDate,
        caseStatus: ci.caseStatus || "",
        partyName: ci.partyName || "",
        petitionerAdvocate: ci.petitionerAdvocate || "",
        respondentAdvocate: ci.respondentAdvocate || "",
        zonalBench: listItem?.zonalBench || "",
        diaryNumber: listItem?.diaryNumber || "",
        registrationDate: toAddCaseDate(listItem?.registrationDate),
      },
    },
  };

  return { caseData, collectionStatus };
};
